'use client';
/**
 * home-tabs (2026-10-28 · Node 「분양라인 지역현장처럼 지역 셀렉을 직관적으로」) — 홈 지역 탭.
 *   1줄: 전국 + 시·도(regions tier 1 — 지금 16곳 · 광주·전남은 «전남광주» 한 칸)(PC 한 줄 · 휴대폰 6칸 격자 — 숨는 칸 0). 2줄: 고른 시·도의 시·군·구 칩(현장 수) + 「{지역} 전체 보기」.
 *   누르면 기존 홈 지역 필터와 «같은 적용»(useApplyHomeRegion: ?r= · 기억 · 칩 동기화 · 계측) → 아래 섹션이 그 지역으로 바뀐다.
 *
 * 규칙:
 *   · 서버 HTML·첫 수화 = 전국 칸이 켜진 같은 마크업(모든 UA 같은 HTML · 클로킹 0). 기억·?r= 강조는 수화 뒤에만.
 *   · 칸은 <button>(?r= 주소를 링크로 늘리지 않는다 — noindex 화면 크롤 낭비 0).
 *   · 현장 수 = regions.site_count 캐시(서버가 넘긴 값 그대로 · 모르면 숫자 생략 · 0 인 시·군·구 칩은 뺀다).
 */
import React from 'react'; // vitest(클래식 JSX) 렌더 검증용
import { useMemo } from 'react';
import { useHomeRegion, useApplyHomeRegion } from './HomeRegionFilter';

export interface HomeTabRegion { code: string; label: string; count: number | null }
export interface HomeTabSido extends HomeTabRegion { hubHref: string; children: HomeTabRegion[] }

/** 화면 순서(분양라인·청약홈과 같은 수도권 → 영남 → 충청 → 호남 → 강원·제주). 모르는 이름은 뒤에 원래 순서대로. */
export const SIDO_TAB_ORDER = ['서울', '경기', '인천', '부산', '울산', '경남', '대구', '경북', '대전', '세종', '충남', '충북', '광주', '전남', '전남광주', '전북', '강원', '제주'] as const;

export function orderSidoTabs<T extends { label: string }>(xs: readonly T[]): T[] {
  const rank = (l: string) => {
    const i = (SIDO_TAB_ORDER as readonly string[]).indexOf(l);
    return i < 0 ? 999 : i;
  };
  return xs.map((x, i) => ({ x, i })).sort((a, b) => rank(a.x.label) - rank(b.x.label) || a.i - b.i).map((v) => v.x);
}

/** 지금 코드 → 켜질 시·도 코드와 시·군·구 코드(순수). */
export function activeTabOf(code: string | null, sidos: readonly HomeTabSido[]): { sido: string | null; sgg: string | null } {
  if (!code) return { sido: null, sgg: null };
  for (const s of sidos) {
    if (s.code === code) return { sido: s.code, sgg: null };
    if (s.children.some((c) => c.code === code)) return { sido: s.code, sgg: code };
  }
  return { sido: null, sgg: null };
}

const fmt = (n: number) => n.toLocaleString('ko-KR');

export default function HomeRegionTabs({ sidos }: { sidos: HomeTabSido[] }) {
  const st = useHomeRegion();
  const apply = useApplyHomeRegion();
  const list = useMemo(() => orderSidoTabs(sidos), [sidos]);
  const code = st.status === 'national' ? null : st.code;
  const on = activeTabOf(code, list);
  const sido = on.sido ? list.find((s) => s.code === on.sido) ?? null : null;
  const kids = sido ? sido.children.filter((c) => c.count == null || c.count > 0) : [];

  return (
    <nav className="clh-rt" aria-label="지역 선택" data-home-region-tabs="" data-kd-hr-dep="">
      <div className="ds2-wrap clh-rt__in">
        <p className="clh-rt__h" id="clh-rt-h">지역별로 보기</p>
        <div className="clh-rt__box">
        <ul className="clh-rt__sido" aria-labelledby="clh-rt-h" style={{ ['--n' as string]: list.length + 1 } as React.CSSProperties}>
          <li>
            <button type="button" aria-pressed={!on.sido} data-on={!on.sido ? '' : undefined} onClick={() => apply(null, 'home-tabs')}>전국</button>
          </li>
          {list.map((s) => (
            <li key={s.code}>
              <button
                type="button" aria-pressed={on.sido === s.code} data-on={on.sido === s.code ? '' : undefined} data-code={s.code}
                onClick={() => apply({ code: s.code, sido: s.label }, 'home-tabs')}
              >
                {s.label}
              </button>
            </li>
          ))}
        </ul>
        {sido ? (
          <div className="clh-rt__sub" data-home-region-sub={sido.code}>
            <ul aria-label={`${sido.label} 시·군·구`}>
              <li>
                <button type="button" aria-pressed={!on.sgg} data-on={!on.sgg ? '' : undefined} onClick={() => apply({ code: sido.code, sido: sido.label }, 'home-tabs-sgg')}>
                  {sido.label} 전체{sido.count != null ? <span>{fmt(sido.count)}</span> : null}
                </button>
              </li>
              {kids.map((c) => (
                <li key={c.code}>
                  <button
                    type="button" aria-pressed={on.sgg === c.code} data-on={on.sgg === c.code ? '' : undefined} data-code={c.code}
                    onClick={() => apply({ code: c.code, sido: sido.label }, 'home-tabs-sgg')}
                  >
                    {c.label}{c.count != null ? <span>{fmt(c.count)}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
            <a className="clh-rt__hub" href={st.status === 'ready' && st.data.hubHref ? st.data.hubHref : sido.hubHref} data-home-region-hub="">
              {st.status === 'ready' && st.data.label ? st.data.label : sido.label} 현장 전체 <span aria-hidden="true">→</span>
            </a>
          </div>
        ) : null}
        </div>
      </div>
    </nav>
  );
}
